import { useEffect } from "react";
import LegalFooter from "@/components/LegalFooter";

export default function Terms() {
  useEffect(() => {
    document.title = "Terms of Service — Comms Fusion Pro";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute("content", "Terms of Service for Comms Fusion Pro — rules and conditions for using our WhatsApp Business platform.");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin + "/terms";
  }, []);

  const updated = "May 5, 2026";

  return (
    <>
      <main className="min-h-screen bg-background text-foreground py-12 px-6">
        <article className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
          <p className="text-sm text-muted-foreground mb-8">Last updated: {updated}</p>

          <section className="space-y-6 text-sm leading-relaxed">
            <p>
              These Terms of Service ("Terms") govern your access to and use of Comms
              Fusion Pro (the "Service"). By using the Service, you agree to these Terms.
            </p>

            <h2 className="text-xl font-semibold mt-8">1. Eligibility</h2>
            <p>You must be at least 16 years old and authorized to bind the business you represent.</p>

            <h2 className="text-xl font-semibold mt-8">2. Account & Security</h2>
            <p>
              You are responsible for safeguarding your credentials and for all activity
              under your account. Notify us immediately of any unauthorized use.
            </p>

            <h2 className="text-xl font-semibold mt-8">3. Acceptable Use</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>No spam, unsolicited bulk messaging, or violation of WhatsApp Business Messaging Policy.</li>
              <li>No illegal, harmful, deceptive, or infringing content.</li>
              <li>No reverse-engineering, scraping, or interfering with the Service.</li>
              <li>You must obtain valid opt-in consent from contacts before messaging them.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8">4. WhatsApp & Meta Compliance</h2>
            <p>
              The Service uses the WhatsApp Business Platform provided by Meta. You agree
              to comply with the{" "}
              <a className="text-primary underline" href="https://www.whatsapp.com/legal/business-policy" target="_blank" rel="noreferrer">WhatsApp Business Messaging Policy</a>,{" "}
              <a className="text-primary underline" href="https://business.whatsapp.com/policy" target="_blank" rel="noreferrer">Commerce Policy</a>, and Meta Platform Terms. Violations may result in suspension by Meta or by us.
            </p>

            <h2 className="text-xl font-semibold mt-8">5. Subscriptions & Fees</h2>
            <p>
              Paid plans are billed in advance and are non-refundable except where required
              by law. Messaging fees charged by Meta are passed through or billed separately
              based on your plan.
            </p>

            <h2 className="text-xl font-semibold mt-8">6. Customer Data</h2>
            <p>
              You retain ownership of contacts, messages, and content you upload ("Customer
              Data"). You grant us a limited license to host and process Customer Data solely
              to provide the Service. You are the data controller; we act as processor.
            </p>

            <h2 className="text-xl font-semibold mt-8">7. Suspension & Termination</h2>
            <p>
              We may suspend or terminate access for breach of these Terms, risk to the
              platform, or non-payment. You may terminate at any time by closing your workspace.
            </p>

            <h2 className="text-xl font-semibold mt-8">8. Disclaimers</h2>
            <p>
              The Service is provided "as is" without warranties of any kind. We do not
              guarantee message deliverability, which depends on Meta and recipient devices.
            </p>

            <h2 className="text-xl font-semibold mt-8">9. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, our total liability for any claim
              arising from the Service shall not exceed the fees paid by you in the 12
              months preceding the event.
            </p>

            <h2 className="text-xl font-semibold mt-8">10. Indemnification</h2>
            <p>
              You agree to indemnify and hold us harmless from claims arising out of your
              Customer Data, your use of the Service, or your violation of these Terms.
            </p>

            <h2 className="text-xl font-semibold mt-8">11. Changes</h2>
            <p>We may update these Terms. Continued use after notice constitutes acceptance.</p>

            <h2 className="text-xl font-semibold mt-8">12. Governing Law</h2>
            <p>These Terms are governed by the laws of the operator's jurisdiction, without regard to conflict-of-law principles.</p>

            <h2 className="text-xl font-semibold mt-8">13. Contact</h2>
            <p>
              <a href="mailto:support@klicko.org" className="text-primary underline">
                support@klicko.org
              </a>
            </p>
          </section>
        </article>
      </main>
      <LegalFooter />
    </>
  );
}
